import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Alert from '../components/ui/Alert';
import FieldToolbar from '../components/editor/FieldToolbar';
import DraggableField from '../components/editor/DraggableField';
import FieldAssignment from '../components/signature/FieldAssignment';
import RecipientManager from '../components/signature/RecipientManager';
import SendReview from '../components/signature/SendReview';
import { useDocuments } from '../contexts/DocumentContext';
import { useAuth } from '../contexts/AuthContext';
import { generateFieldId } from '../utils/idGenerator';
import './EditorPage.css';

// Set up the worker for PDF.js
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const EditorPage = () => {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const { documents, updateDocument, loading, error: contextError } = useDocuments();
  const documentContainerRef = useRef(null);
  
  const [document, setDocument] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [fields, setFields] = useState([]);
  const [activeFieldType, setActiveFieldType] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pages, setPages] = useState([]);
  const [isDraggingFromToolbar, setIsDraggingFromToolbar] = useState(false);
  const [dragOverPage, setDragOverPage] = useState(null);
  const [dragSourceType, setDragSourceType] = useState(null);
  const [recipients, setRecipients] = useState([{ email: '', color: '#4361ee' }]);
  const [activeRecipient, setActiveRecipient] = useState(0);
  
  // New state for multi-party signature flow
  const [currentStep, setCurrentStep] = useState('editor'); // editor, recipients, fields, review
  const { user } = useAuth();
  const location = useLocation();
  
  // Check if we have an initial step from navigation
  useEffect(() => {
    if (location.state?.initialStep) {
      setCurrentStep(location.state.initialStep);
    }
  }, [location]);

  // Load document data
  useEffect(() => {
    if (documents.length > 0) {
      const doc = documents.find(d => d.id === documentId);
      if (doc) {
        setDocument(doc);
        // Make a deep copy of the fields to avoid reference issues
        setFields(doc.fields ? JSON.parse(JSON.stringify(doc.fields)) : []);
        
        // Initialize recipients based on signature type
        if (doc.recipients && doc.recipients.length > 0) {
          // Use existing recipients if available
          setRecipients(doc.recipients);
        } else if (doc.signatureType === 'self') {
          // Just Me - Self-sign only
          setRecipients([{ 
            email: doc.selfSignEmail || 'Self', 
            color: '#4361ee',
            isSelf: true
          }]);
        } else if (doc.signatureType === 'me_and_others') {
          // Me and Others - Owner is first recipient
          setRecipients([
            { 
              email: doc.selfSignEmail || 'You (Owner)', 
              color: '#4361ee',
              isOwner: true
            },
            { email: '', color: '#7209b7' }
          ]);
        } else if (doc.signatureType === 'others_only') {
          // Others Only - Start with no recipients
          setRecipients([]);
        } else if (doc.isSelfSign) {
          // Legacy self-sign support
          setRecipients([{ 
            email: doc.selfSignEmail || 'Self', 
            color: '#4361ee',
            isSelf: true
          }]);
        }
        
        setIsLoading(false);
      } else {
        setError('Document not found');
        setIsLoading(false);
      }
    }
  }, [documentId, documents]);

  // Handle PDF document loading
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPages(Array.from(new Array(numPages), (_, index) => index + 1));
  };

  // Handle adding a new field
  const handleAddField = (fieldType) => {
    setActiveFieldType(fieldType);
  };

  // Handle toolbar field drag start
  const handleToolbarDragStart = (fieldType) => {
    setActiveFieldType(fieldType);
    setIsDraggingFromToolbar(true);
    setDragSourceType('new');
  };

  // Handle document drag over
  const handleDragOver = (e, pageNumber) => {
    e.preventDefault();
    // Set the drag over page for visual feedback
    setDragOverPage(pageNumber);
    
    // Set the dropEffect based on the drag source type
    const effect = dragSourceType === 'new' ? 'copy' : 'move';
    e.dataTransfer.dropEffect = effect;
  };

  // Handle document drag leave
  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOverPage(null);
  };

  // Handle document drop
  const handleDrop = (e, pageNumber) => {
    e.preventDefault();
    
    // Reset drag over state
    setDragOverPage(null);
    
    // Get the drop position relative to the document page
    const pageElement = e.currentTarget;
    const rect = pageElement.getBoundingClientRect();
    
    // Check if this is a new field from toolbar or an existing field being moved
    const isExistingField = dragSourceType === 'existing';
    
    // Try to get the JSON data from the dataTransfer
    let fieldId, fieldType;
    
    try {
      const jsonData = e.dataTransfer.getData('application/json');
      if (jsonData) {
        const fieldData = JSON.parse(jsonData);
        fieldId = fieldData.fieldId;
        fieldType = fieldData.fieldType;
      }
    } catch (err) {
      // Try to get data from text/plain as fallback
      const plainData = e.dataTransfer.getData('text/plain');
      
      if (plainData && plainData.includes(':')) {
        // Format might be "id:type:isNew" or "type:new"
        const parts = plainData.split(':');
        if (parts.length >= 2) {
          if (parts[1] === 'new') {
            fieldType = parts[0];
          } else {
            fieldId = parts[0];
            fieldType = parts[1];
          }
        }
      } else if (plainData) {
        // Just a simple ID or type
        if (isExistingField) {
          fieldId = plainData;
          // Try to find the field type from existing fields
          const field = fields.find(f => f.id === plainData);
          if (field) {
            fieldType = field.type;
          }
        } else {
          fieldType = plainData;
        }
      }
    }
    
    // If we still don't have a field type, use the active field type
    if (!fieldType && activeFieldType) {
      fieldType = activeFieldType;
    }
    
    // If we don't have either field ID or type, we can't proceed
    if (!fieldId && !fieldType) {
      return;
    }
    
    // Get the offset from the dataTransfer object or use default values
    let offsetX = 0, offsetY = 0;
    try {
      const offsetData = e.dataTransfer.getData('offset');
      if (offsetData) {
        const offset = JSON.parse(offsetData);
        offsetX = offset.x || 0;
        offsetY = offset.y || 0;
      }
    } catch (err) {
      // Try to get individual offset values
      try {
        offsetX = parseInt(e.dataTransfer.getData('offsetX')) || 0;
        offsetY = parseInt(e.dataTransfer.getData('offsetY')) || 0;
      } catch (offsetErr) {
        // Continue with default values
      }
    }
    
    // Get field dimensions based on type
    let fieldWidth = 120, fieldHeight = 40;
    if (fieldType === 'signature') {
      fieldWidth = 150;
      fieldHeight = 60;
    } else if (fieldType === 'initial') {
      fieldWidth = 60;
      fieldHeight = 40;
    }
    
    // Calculate the position, accounting for the offset and centering the field
    const x = offsetX ? (e.clientX - rect.left - offsetX) : (e.clientX - rect.left - (fieldWidth / 2));
    const y = offsetY ? (e.clientY - rect.top - offsetY) : (e.clientY - rect.top - (fieldHeight / 2));
    
    // Ensure position is within bounds
    const maxWidth = rect.width;
    const maxHeight = rect.height;
    
    // Constrain position to ensure field stays within document bounds
    const constrainedX = Math.max(0, Math.min(x, maxWidth - fieldWidth));
    const constrainedY = Math.max(0, Math.min(y, maxHeight - fieldHeight));
    
    // If this is an existing field being moved
    if (isExistingField && fieldId && fields.some(field => field.id === fieldId)) {
      // Update the existing field's position
      handleFieldPositionChange(
        fieldId, 
        { x: constrainedX, y: constrainedY }, 
        pageNumber
      );
    } 
    // Otherwise, this is a new field from the toolbar
    else if (fieldType) {
      // Create a new field
      const newField = {
        id: generateFieldId(),
        type: fieldType,
        position: { x: constrainedX, y: constrainedY },
        pageNumber,
        createdAt: new Date().toISOString()
      };
      
      // Add the field to the state
      setFields(prevFields => [...prevFields, newField]);
    }
    
    // Reset active field type and drag state after drop
    setActiveFieldType(null);
    setIsDraggingFromToolbar(false);
    setDragSourceType(null);
  };

  // Handle clicking on the document to place a field
  const handleDocumentClick = (e, pageNumber) => {
    if (!activeFieldType) return;
    
    // Get the click position relative to the document page
    const pageElement = e.currentTarget;
    const rect = pageElement.getBoundingClientRect();
    
    // Get field dimensions based on type
    let fieldWidth = 120, fieldHeight = 40;
    if (activeFieldType === 'signature') {
      fieldWidth = 150;
      fieldHeight = 60;
    } else if (activeFieldType === 'initial') {
      fieldWidth = 60;
      fieldHeight = 40;
    }
    
    // Calculate position with the cursor at the center of the field
    const x = e.clientX - rect.left - (fieldWidth / 2);
    const y = e.clientY - rect.top - (fieldHeight / 2);
    
    // Ensure position is within bounds
    const maxWidth = rect.width;
    const maxHeight = rect.height;
    const constrainedX = Math.max(0, Math.min(x, maxWidth - fieldWidth));
    const constrainedY = Math.max(0, Math.min(y, maxHeight - fieldHeight));
    
    // Create a new field
    const newField = {
      id: generateFieldId(),
      type: activeFieldType,
      position: { x: constrainedX, y: constrainedY },
      pageNumber,
      createdAt: new Date().toISOString(),
      recipientIndex: activeRecipient,
      recipientEmail: recipients[activeRecipient]?.email || null
    };
    
    // Add the field to the state
    setFields(prevFields => [...prevFields, newField]);
    
    // Deselect the field tool after placing a field
    setActiveFieldType(null);
  };

  // Handle field deletion
  const handleFieldDelete = (fieldId) => {
    setFields(prevFields => prevFields.filter(field => field.id !== fieldId));
  };

  // Handle field position change
  const handleFieldPositionChange = (fieldId, newPosition, pageNumber) => {
    setFields(prevFields => 
      prevFields.map(field => 
        field.id === fieldId 
          ? { ...field, position: newPosition, pageNumber: pageNumber || field.pageNumber } 
          : field
      )
    );
  };

  // Handle field size change
  const handleFieldSizeChange = (fieldId, newSize) => {
    setFields(prevFields => 
      prevFields.map(field => 
        field.id === fieldId 
          ? { ...field, size: newSize } 
          : field
      )
    );
  };

  // Handle saving the document with fields
  const handleSave = async () => {
    try {
      setIsLoading(true);
      
      // Filter out empty recipients
      const filteredRecipients = recipients.filter(r => r.email.trim() !== '');
      
      // Store recipient information directly in each field
      const enhancedFields = fields.map(field => {
        // Get the recipient for this field
        const recipientIndex = field.recipientIndex || 0;
        const recipient = filteredRecipients[recipientIndex] || filteredRecipients[0] || { email: '', color: '#4361ee' };
        
        // Return enhanced field with recipient data embedded
        return {
          ...field,
          recipientIndex: recipientIndex,
          recipientEmail: field.recipientEmail || recipient.email,
          recipientColor: field.recipientColor || recipient.color,
          _recipients: JSON.stringify(filteredRecipients.map(r => ({ 
            email: r.email, 
            color: r.color 
          })))
        };
      });
      
      // Store recipients as a special field in the fields array
      const recipientsField = {
        id: 'recipients-metadata',
        type: 'metadata',
        isMetadata: true,
        recipients: filteredRecipients,
        createdAt: new Date().toISOString()
      };
      
      // Add the recipients field to the enhanced fields array
      const fieldsWithoutMetadata = enhancedFields.filter(f => f.id !== 'recipients-metadata');
      const finalFields = [...fieldsWithoutMetadata, recipientsField];
      
      // Make a single update call with just the fields
      await updateDocument(documentId, { 
        fields: finalFields
      });
      
      setIsLoading(false);
      
      // Determine next step based on signature type
      if (document?.signatureType === 'self') {
        // Just Me - Go directly to sign page
        navigate(`/sign/${documentId}`);
      } else if (document?.signatureType === 'me_and_others') {
        // Me and Others - Go to send page, then will redirect to sign
        navigate(`/send/${documentId}`, { 
          state: { 
            recipients: filteredRecipients.filter(r => !r.isOwner).map(r => r.email),
            includeOwner: true,
            fromEditor: true
          } 
        });
      } else if (document?.isSelfSign) {
        // Legacy self-sign support
        navigate(`/sign/${documentId}`);
      } else {
        // Others Only - Go to send page
        navigate(`/send/${documentId}`, { 
          state: { 
            recipients: filteredRecipients.map(r => r.email),
            fromEditor: true
          } 
        });
      }
    } catch (err) {
      console.error('Error saving document:', err);
      setError('Failed to save document. Please try again.');
      setIsLoading(false);
    }
  };
  
  // Add a new recipient
  const handleAddRecipient = () => {
    // Generate a random color for the new recipient
    const colors = ['#4361ee', '#3a0ca3', '#7209b7', '#f72585', '#4cc9f0', '#4895ef', '#560bad', '#f3722c', '#f8961e', '#43aa8b'];
    const randomColor = colors[Math.floor(Math.random() * colors.length)];
    
    setRecipients([...recipients, { email: '', color: randomColor }]);
  };
  
  // Remove a recipient
  const handleRemoveRecipient = (index) => {
    // Don't remove if it's the only recipient
    if (recipients.length <= 1) return;
    
    // Remove the recipient
    const newRecipients = [...recipients];
    newRecipients.splice(index, 1);
    setRecipients(newRecipients);
    
    // Update active recipient if needed
    if (activeRecipient >= newRecipients.length) {
      setActiveRecipient(newRecipients.length - 1);
    }
    
    // Update fields assigned to this recipient
    setFields(prevFields => 
      prevFields.map(field => {
        if (field.recipientIndex === index) {
          // Reassign to the first recipient
          return { 
            ...field, 
            recipientIndex: 0,
            recipientEmail: newRecipients[0]?.email || null
          };
        } else if (field.recipientIndex > index) {
          // Adjust index for fields assigned to recipients after the removed one
          return { 
            ...field, 
            recipientIndex: field.recipientIndex - 1,
            recipientEmail: newRecipients[field.recipientIndex - 1]?.email || null
          };
        }
        return field;
      })
    );
  };
  
  // Update recipient email
  const handleRecipientEmailChange = (index, email) => {
    const newRecipients = [...recipients];
    newRecipients[index].email = email;
    setRecipients(newRecipients);
    
    // Update fields assigned to this recipient
    setFields(prevFields => 
      prevFields.map(field => {
        if (field.recipientIndex === index) {
          return { ...field, recipientEmail: email };
        }
        return field;
      })
    );
  };

  // Handle step navigation
  const handleNextStep = () => {
    // For self-sign, always go directly to sign page
    if (document.signatureType === 'self' || document.isSelfSign) {
      handleSave();
      return;
    }
    
    // For multi-party signatures
    if (currentStep === 'editor') {
      // Go to recipients step first
      setCurrentStep('recipients');
    } else if (currentStep === 'recipients') {
      // After recipients are entered, go to field assignment
      setCurrentStep('fields');
    } else if (currentStep === 'fields') {
      // After fields are assigned, go to review
      setCurrentStep('review');
    } else if (currentStep === 'review') {
      // After review, save and send
      handleSave();
    }
  };
  
  const handlePreviousStep = () => {
    if (currentStep === 'recipients') {
      setCurrentStep('editor');
    } else if (currentStep === 'fields') {
      setCurrentStep('recipients');
    } else if (currentStep === 'review') {
      setCurrentStep('fields');
    }
  };
  
  // Handle send
  const handleSend = (sendData) => {
    // Update message if provided
    if (sendData.message) {
      // Store message in document
      updateDocument(documentId, { message: sendData.message });
    }
    
    // Continue to save and send
    handleSave();
  };

  // Render content based on current step
  const renderStepContent = () => {
    switch (currentStep) {
      case 'editor':
        return (
          <div className="editor-content">
            <div className="editor-sidebar">
              <FieldToolbar 
                onAddField={handleAddField} 
                onDragStart={handleToolbarDragStart}
              />
            </div>
            
            <div className="document-preview" ref={documentContainerRef}>
              <Document
                file={document.file_data && document.file_data.startsWith('STORAGE_URL:') 
                  ? document.file_data.substring('STORAGE_URL:'.length) 
                  : document.fileData}
                onLoadSuccess={onDocumentLoadSuccess}
                loading={<div className="loading-spinner"></div>}
                error={<Alert 
                  type="error" 
                  message="Failed to load PDF document" 
                  title=""
                  icon={null}
                  action={null}
                  onDismiss={() => {}}
                />}
              >
                <div className="document-pages-container">
                  {pages.map(pageNumber => (
                    <div key={pageNumber} className="document-page-wrapper">
                      <div 
                        className={`document-page-overlay ${dragOverPage === pageNumber ? 'drag-over' : ''}`}
                        onClick={(e) => handleDocumentClick(e, pageNumber)}
                        onDragOver={(e) => handleDragOver(e, pageNumber)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, pageNumber)}
                      >
                        {fields
                          .filter(field => field.pageNumber === pageNumber)
                          .map(field => (
                            <DraggableField
                              key={field.id}
                              id={field.id}
                              type={field.type}
                              position={field.position}
                              size={field.size}
                              onPositionChange={handleFieldPositionChange}
                              onSizeChange={handleFieldSizeChange}
                              onDelete={handleFieldDelete}
                              pageNumber={pageNumber}
                              onDragStart={() => setDragSourceType('existing')}
                              borderColor={recipients[field.recipientIndex]?.color}
                            />
                          ))
                        }
                      </div>
                      <Page 
                        pageNumber={pageNumber} 
                        width={800}
                        className="document-page"
                      />
                    </div>
                  ))}
                </div>
              </Document>
            </div>
          </div>
        );
        
      case 'recipients':
        return (
          <RecipientManager
            recipients={recipients}
            onAddRecipient={(newRecipient) => {
              // Generate a random color for the new recipient
              const colors = ['#4361ee', '#3a0ca3', '#7209b7', '#f72585', '#4cc9f0', '#4895ef', '#560bad', '#f3722c', '#f8961e', '#43aa8b'];
              const randomColor = colors[Math.floor(Math.random() * colors.length)];
              
              // Add the new recipient with a color
              const recipientWithColor = {
                ...newRecipient,
                color: randomColor
              };
              
              setRecipients([...recipients, recipientWithColor]);
            }}
            onRemoveRecipient={(index) => {
              const newRecipients = [...recipients];
              newRecipients.splice(index, 1);
              setRecipients(newRecipients);
            }}
            onUpdateRecipient={(index, updatedRecipient) => {
              const newRecipients = [...recipients];
              newRecipients[index] = { ...newRecipients[index], ...updatedRecipient };
              setRecipients(newRecipients);
            }}
            onContinue={() => handleNextStep()}
            currentUser={user}
          />
        );
        
      case 'fields':
        return (
          <FieldAssignment
            document={document}
            recipients={recipients}
            currentUser={user}
            onAssignField={(fieldId, recipientEmail) => {
              // Find the recipient index
              const recipientIndex = recipients.findIndex(r => r.email === recipientEmail);
              if (recipientIndex === -1) return;
              
              // Update the field
              setFields(prevFields => 
                prevFields.map(field => 
                  field.id === fieldId 
                    ? { 
                        ...field, 
                        recipientIndex, 
                        recipientEmail,
                        recipientColor: recipients[recipientIndex].color
                      } 
                    : field
                )
              );
            }}
            onContinue={handleNextStep}
          />
        );
        
      case 'review':
        return (
          <SendReview
            document={document}
            recipients={recipients}
            currentUser={user}
            onSend={handleSend}
          />
        );
        
      default:
        return null;
    }
  };
  
  // Get step title
  const getStepTitle = () => {
    switch (currentStep) {
      case 'editor':
        return 'Add Signature Fields';
      case 'recipients':
        return 'Manage Recipients';
      case 'fields':
        return 'Assign Fields to Recipients';
      case 'review':
        return 'Review and Send';
      default:
        return document.name;
    }
  };
  
  // Get continue button text
  const getContinueButtonText = () => {
    if (currentStep === 'editor') {
      if (document?.signatureType === 'self' || document?.isSelfSign) {
        return 'Continue to Sign';
      } else {
        return 'Continue to Recipients';
      }
    } else if (currentStep === 'recipients') {
      return 'Continue to Field Assignment';
    } else if (currentStep === 'fields') {
      return 'Continue to Review';
    } else if (currentStep === 'review') {
      if (document?.signatureType === 'me_and_others') {
        return 'Sign & Send to Others';
      } else if (document?.signatureType === 'self' || document?.isSelfSign) {
        return 'Continue to Sign';
      } else {
        return 'Send for Signature';
      }
    }
    
    return 'Continue';
  };

  // Render step indicator
  const renderStepIndicator = () => {
    // Only show step indicator for multi-party signature flows
    if (document?.signatureType === 'self' || document?.isSelfSign) {
      return null;
    }
    
    const steps = [
      { id: 'editor', label: 'Add Fields' },
      { id: 'recipients', label: 'Recipients' },
      { id: 'fields', label: 'Assign Fields' },
      { id: 'review', label: 'Review & Send' }
    ];
    
    const currentStepIndex = steps.findIndex(step => step.id === currentStep);
    
    return (
      <div className="step-indicator">
        {steps.map((step, index) => {
          const isActive = step.id === currentStep;
          const isCompleted = index < currentStepIndex;
          
          return (
            <div 
              key={step.id} 
              className={`step-item ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
            >
              <div className="step-number">
                {isCompleted ? '✓' : index + 1}
              </div>
              <div className="step-label">{step.label}</div>
            </div>
          );
        })}
      </div>
    );
  };

  // Loading state
  if (isLoading || loading || (!document && documents.length > 0)) {
    return (
      <div className="editor-page">
        <div className="container">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading document editor...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || contextError) {
    return (
      <div className="editor-page">
        <div className="container">
          <Alert 
            type="error" 
            title="Error" 
            message={error || contextError} 
            icon={null}
            action={null}
            onDismiss={() => {}}
          />
          <div className="text-center mt-lg">
            <Button onClick={() => navigate('/upload')}>
              Upload Another Document
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Document not found
  if (!document && !isLoading && !loading && documents.length > 0) {
    return (
      <div className="editor-page">
        <div className="container">
          <Alert 
            type="error" 
            title="Document Not Found" 
            message="The document you're looking for doesn't exist or has been deleted." 
            icon={null}
            action={null}
            onDismiss={() => {}}
          />
          <div className="text-center mt-lg">
            <Button onClick={() => navigate('/upload')}>
              Upload a Document
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Document not loaded
  if (!document) {
    return (
      <div className="editor-page">
        <div className="container">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Preparing document for editing...</p>
          </div>
        </div>
      </div>
    );
  }

  // Main render
  return (
    <div className="editor-page">
      <div className="container">
        <div className="editor-header">
          <h1 className="page-title">{getStepTitle()}</h1>
          <div className="editor-actions">
            {currentStep !== 'editor' && (
              <Button 
                variant="secondary" 
                onClick={handlePreviousStep}
              >
                Back
              </Button>
            )}
            {currentStep === 'editor' && (
              <Button 
                variant="secondary" 
                onClick={() => navigate(-1)}
              >
                Cancel
              </Button>
            )}
            {currentStep !== 'review' && currentStep !== 'fields' && (
              <Button 
                onClick={handleNextStep}
                loading={isLoading}
              >
                {getContinueButtonText()}
              </Button>
            )}
          </div>
        </div>
        
        {renderStepIndicator()}
        {renderStepContent()}
      </div>
    </div>
  );
};

export default EditorPage;
